import { useCallback, useMemo, useState } from 'react';
import type { ModalListItem } from 'src/components/ModalContainer';
import { SelectSiteDialog } from 'src/components/SelectSiteDialog/select-site-dialog';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { Address } from 'src/graphql/entities/address/address';
import { DEFAULT_PAGINATION_LIMIT, useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { useClientSiteAssociation } from 'src/pages/Clients/use-client-site-association';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';

export const UpdateClientToAddressModal = () => {
  const { closeModal } = useModalMethods(Modals.UpdateClientToAddress);

  const { isOpened, modalProps } = useModalState(Modals.UpdateClientToAddress);
  const { client } = modalProps || {};

  const [searchValue, setSearchValue] = useState('');

  const addressesFilters = useMemo<ConditionInput[]>(
    () => [
      {
        attribute: 'clients/clientId',
        operator: 'in',
        value: client ? client.clientId : '',
      },
    ],
    [client],
  );

  const { records: addresses, isLoading: loadingAddresses } =
    useSearch({
      entityName: EntityNames.Site,
      searchTerm: searchValue,
      showResultsWhenEmptySearchTerm: true,
      filter: addressesFilters,
      limit: DEFAULT_PAGINATION_LIMIT * 10,
    }) ?? {};

  const clientAddresses = useMemo(
    () => (addresses ? Object.values(addresses) : []),
    [addresses],
  );

  const sitesToAddFilter = useMemo(() => {
    const initialFilters = [
      // This filter should be present, since it is also used in multi-tenancy mode
      // when partner-id header is not included in the request.
      {
        attribute: 'partnerId',
        operator: 'in',
        value: [client?.partnerId, PLATFORM_PARTNER_ID].join(','),
      },
    ];

    return [
      {
        attribute: 'addressId',
        operator: 'not_in',
        value: clientAddresses.map((site) => site.addressId).join(','),
      },
      ...initialFilters,
    ];
  }, [client?.partnerId, clientAddresses]);

  const { records: sitesToAdd, isLoading: loadingSitesToAdd } =
    useSearch({
      entityName: EntityNames.Site,
      searchTerm: searchValue,
      showResultsWhenEmptySearchTerm: true,
      filter: sitesToAddFilter,
    }) ?? {};

  const filteredSitesToAdd = useMemo(
    () =>
      sitesToAdd
        // Remove duplicated elements
        ?.filter(
          (site, index, arr) => arr.findIndex((s) => s.addressId === site.addressId) === index,
        )
        .map((site) => ({
          label: site.fullAddress ?? '',
          ...site,
        })) ?? [],
    [sitesToAdd],
  );

  const filteredOptions = useMemo<ModalListItem[]>(
    () =>
      filteredSitesToAdd.map((address) => ({
        id: address.addressId,
        title: address.fullAddress ?? '',
      })),
    [filteredSitesToAdd],
  );

  const { associateSiteToClient } = useClientSiteAssociation(client!);

  const onItemSelect = useCallback(
    (item: ModalListItem, createdSite?: Address) => {
      closeModal();
      const addressId = item.id;
      const clientAddress = clientAddresses.find((item) => item.addressId === addressId);
      const siteToAdd =
        createdSite ?? filteredSitesToAdd.find((item) => item.addressId === addressId);

      const newValue = siteToAdd || clientAddress;

      if (newValue) {
        void associateSiteToClient(newValue);
      }
    },
    [associateSiteToClient, clientAddresses, closeModal, filteredSitesToAdd],
  );

  const loading = loadingAddresses || loadingSitesToAdd;

  return (
    <SelectSiteDialog
      open={isOpened}
      title="Add site"
      testId="change-client-site-modal"
      loading={loading}
      filters={addressesFilters}
      options={filteredOptions}
      onSelect={onItemSelect}
      partnerId={client?.partnerId}
      onClose={closeModal}
      onSearchChange={setSearchValue}
    />
  );
};
